import { create } from "zustand";
import { _Product_liquid, _Variant_liquid } from "./_types";
import { CloseIcon } from "./icons";
import { immer } from "zustand/middleware/immer";
import { useEffect, useMemo } from "react";
import { unescape } from "./utils";

export type VariantDetailModalData = {
  title?: string;
  description?: string;
  imageSrc?: string;
  imageAlt?: string;
  composition?: string,
  durability?: string,
  careInstructions?: string,
}

export type VariantDetailsModalState = {
  open: boolean;
  toggleModal: (open: boolean) => void;
  setProductData: (modalData: VariantDetailModalData) => void;
} & VariantDetailModalData;

export const useVariantDetailsModal = create(
  immer<VariantDetailsModalState>((set) => ({
    open: false,
    title: undefined,
    description: undefined,
    imageSrc: undefined,
    imageAlt: undefined,
    composition: undefined,
    durability: undefined,
    careInstructions: undefined,
    toggleModal: (open) => set((state) => ({ ...state, open })),
    setProductData: (data) => set((state) => ({ ...state, ...data })),
  }))
);

export const VariantDetailsModal = () => {
  const { open, imageSrc, imageAlt, title, composition, description, durability, careInstructions, toggleModal } = useVariantDetailsModal();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        toggleModal(false);
      }
    };
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.addEventListener("keydown", handleKeyDown);
      };
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open]);
  
  return (
    <div
      className={`fixed top-0 right-0 left-0 bottom-0 z-50 flex justify-end isolate ${
        open ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      <div
        className={`absolute top-0 right-0 left-0 bottom-0 bg-white transition-opacity duration-300 ${
          open ? "opacity-50" : "opacity-0"
        }`}
        onClick={() => toggleModal(false)}
      ></div>

      <div
        className={`flex flex-col bg-white shadow-md w-[428px] max-w-full relative z-10 overflow-hidden transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="py-3 px-8 flex justify-between items-center shadow-md shrink-0">
          <h4 className="text-lg">About This Selection</h4>
          <button
            className="text-primary-bg"
            onClick={() => toggleModal(false)}
          >
            <CloseIcon className="w-[35px] h-[35px]" />
          </button>
        </div>

        <div className="px-8 pt-8 pb-16 flex flex-col gap-8 overflow-auto flex-1">
          <div className="flex flex-col gap-6">
            <h3 className="text-4xl font-h3">{title}</h3>
            <img src={imageSrc} alt={imageAlt} />
            {description && 
              <div dangerouslySetInnerHTML={{ __html: unescape(description as string) }}></div>
            }
          </div>
          <div className="flex flex-col gap-6">
            {composition && 
              <div className="flex flex-col gap-0 pb-6 border-b-[1px] border-solid border-[#ccc]">
                <h5 className="text-lg font-semibold mb-2">Composition</h5>
                <div dangerouslySetInnerHTML={{ __html: unescape(composition as string) }}></div>
              </div>
            }
            {durability && 
              <div className="flex flex-col gap-0 pb-6 border-b-[1px] border-solid border-[#ccc]">
                <h5 className="text-lg font-semibold mb-2">Durability</h5>
                <div dangerouslySetInnerHTML={{ __html: unescape(durability as string) }}></div>
              </div>
            }
            {careInstructions && 
              <div className="flex flex-col gap-0 pb-6 border-b-[1px] border-solid border-[#ccc]">
                <h5 className="text-lg font-semibold mb-2">Care</h5>
                <div dangerouslySetInnerHTML={{ __html: unescape(careInstructions as string) }}></div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
};
